'use client';

import { useState } from 'react';

interface CustomRequestFormProps {
  productName?: string;
  productUrl?: string;
}

export default function CustomRequestForm({ productName, productUrl }: CustomRequestFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    metal: '',
    plating: '',
    gemstones: '',
    needCad: false,
    needNda: false,
    brandLogo: false,
    referenceLink: '',
    notes: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!formData.name || !formData.email || !formData.phone) {
      setError('Please fill in the required fields (Name, Email, and Phone).');
      return;
    }

    // Folds the structured fields into one message block, matching how the
    // live site's form composes its single "message" field before posting.
    const lines = [
      productName ? `Product: ${productName}` : null,
      productUrl ? `Product Link: ${productUrl}` : null,
      formData.metal ? `Metal Required: ${formData.metal}` : null,
      formData.plating ? `Plating Requirement: ${formData.plating}` : null,
      formData.gemstones ? `Gemstone Requirement: ${formData.gemstones}` : null,
      `Need CAD: ${formData.needCad ? 'Yes' : 'No'}`,
      `Need NDA Agreement: ${formData.needNda ? 'Yes' : 'No'}`,
      `Brand Logo Engraving: ${formData.brandLogo ? 'Yes' : 'No'}`,
      formData.referenceLink ? `Reference Link: ${formData.referenceLink}` : null,
      formData.notes ? `Notes: ${formData.notes}` : null,
    ].filter(Boolean);

    setLoading(true);
    try {
      const res = await fetch('/api/custom-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: lines.join('\n'),
          pageUrl: typeof window !== 'undefined' ? window.location.href : '',
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.message || 'Could not send your request. Please try again.');
        return;
      }
      setSent(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        metal: '',
        plating: '',
        gemstones: '',
        needCad: false,
        needNda: false,
        brandLogo: false,
        referenceLink: '',
        notes: '',
      });
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-8 md:p-10 shadow-sm space-y-8 max-w-7xl mx-auto">

      {/* Header info */}
      <div className="text-center space-y-3">
        <h2 className="text-xl md:text-2xl font-serif font-bold text-gray-800">
          B2B Jewelry Manufacturer for Global Brands
        </h2>
        <p className="text-gray-500 text-xs md:text-sm max-w-4xl mx-auto leading-relaxed">
          Share your design, metal, plating, gemstone, and quantity requirements for a custom manufacturing quotation. We work in sterling silver and solid gold, including precious, semi-precious, lab diamond, and genuine diamond jewelry.
        </p>
        {productName && (
          <p className="text-xs text-[var(--orange-dark)] bg-[var(--orange-light-bg)]/50 inline-block px-4 py-1.5 rounded-full">
            Regarding: <strong>{productName}</strong>
          </p>
        )}
      </div>

      {/* 4 Cards Grid Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-[#fdfaf6] border border-[#f5ebdb] p-4 rounded-xl shadow-xs">
          <h3 className="text-xs font-bold text-amber-900 uppercase tracking-wide mb-1.5">MOQ & Pricing</h3>
          <p className="text-[11px] text-gray-600 leading-relaxed">
            MOQ usually starts from 5 to 10 pieces depending on design. Sample pricing and quantity-based prices are available.
          </p>
        </div>
        <div className="bg-[#fdfaf6] border border-[#f5ebdb] p-4 rounded-xl shadow-xs">
          <h3 className="text-xs font-bold text-amber-900 uppercase tracking-wide mb-1.5">CAD & Production</h3>
          <p className="text-[11px] text-gray-600 leading-relaxed">
            CAD usually takes 3 to 4 working days. Sample production takes 25 to 30 days and bulk production takes 30 to 40 days.
          </p>
        </div>
        <div className="bg-[#fdfaf6] border border-[#f5ebdb] p-4 rounded-xl shadow-xs">
          <h3 className="text-xs font-bold text-amber-900 uppercase tracking-wide mb-1.5">Shipping</h3>
          <p className="text-[11px] text-gray-600 leading-relaxed">
            Shipping: 4 to 7 days worldwide.
          </p>
        </div>
        <div className="bg-[#fdfaf6] border border-[#f5ebdb] p-4 rounded-xl shadow-xs">
          <h3 className="text-xs font-bold text-amber-900 uppercase tracking-wide mb-1.5">Payments</h3>
          <p className="text-[11px] text-gray-600 leading-relaxed">
            We accept payments via PayPal, Payoneer, Wise, and bank transfer.
          </p>
        </div>
      </div>

      {/* Custom designs notice callout banner */}
      <div className="bg-[var(--orange-light-bg)]/50 border border-[var(--border-color)]/50 p-4 rounded-xl text-xs leading-relaxed text-[var(--orange-dark)]">
        Custom designs are processed through CAD development only. We do not provide price estimates without CAD and final design details. NDA agreement and brand logo engraving are available on request.
      </div>

      {sent ? (
        <div className="border border-green-200 bg-green-50 rounded-2xl p-8 text-center space-y-2">
          <p className="text-2xl">✓</p>
          <p className="text-sm font-semibold text-green-800">
            Thank you! Your custom jewelry inquiry has been sent.
          </p>
          <p className="text-xs text-green-700">Our B2B manufacturing team will contact you within 24 hours.</p>
          <button
            onClick={() => setSent(false)}
            className="text-xs font-semibold text-green-800 underline hover:text-green-900 mt-2"
          >
            Send another request
          </button>
        </div>
      ) : (
      <form onSubmit={handleSubmit} className="border border-[#a81c3c]/30 rounded-2xl p-6 md:p-8 space-y-6">

        {error && (
          <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>
        )}

        {/* Row 1: Name, Email, Phone */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-700">Full Name or Business Name <span className="text-red-500">*</span></label>
            <input
              type="text"
              required
              placeholder="Full Name or Business Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-[var(--orange-primary)] bg-[#fafafa]"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-700">Email <span className="text-red-500">*</span></label>
            <input
              type="email"
              required
              placeholder="Your Email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-[var(--orange-primary)] bg-[#fafafa]"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-700">WhatsApp / Phone with ISD Code <span className="text-red-500">*</span></label>
            <input
              type="text"
              required
              placeholder="Example: +1 5551234567"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-[var(--orange-primary)] bg-[#fafafa]"
            />
          </div>
        </div>

        {/* Row 2: Metal, Plating, Gemstones */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-700">Metal Required</label>
            <select
              value={formData.metal}
              onChange={(e) => setFormData({ ...formData, metal: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-[var(--orange-primary)] bg-[#fafafa] text-gray-650"
            >
              <option value="">Select metal / purity</option>
              <option value="925 Silver">92.5 Sterling Silver</option>
              <option value="9K Gold">9K Solid Gold</option>
              <option value="10K Gold">10K Solid Gold</option>
              <option value="14K Gold">14K Solid Gold</option>
              <option value="18K Gold">18K Solid Gold</option>
              <option value="22K Gold">22K Solid Gold</option>
              <option value="Brass">Brass / Bronze</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-700">Plating Requirement</label>
            <select
              value={formData.plating}
              onChange={(e) => setFormData({ ...formData, plating: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-[var(--orange-primary)] bg-[#fafafa] text-gray-650"
            >
              <option value="">Select plating</option>
              <option value="Yellow Gold">Yellow Gold</option>
              <option value="White Gold">White Gold</option>
              <option value="Rose Gold">Rose Gold</option>
              <option value="Natural Silver">Natural Silver</option>
              <option value="Black Rhodium">Black Rhodium</option>
              <option value="1 Micron YG">1 Micron Yellow Gold</option>
              <option value="2 Micron YG">2 Micron Yellow Gold</option>
              <option value="3 Micron YG">3 Micron Yellow Gold</option>
              <option value="No Plating">No Plating</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-700">Gemstone Requirement</label>
            <input
              type="text"
              placeholder="Example: lab diamonds, emeralds, ruby, moissanite"
              value={formData.gemstones}
              onChange={(e) => setFormData({ ...formData, gemstones: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-[var(--orange-primary)] bg-[#fafafa]"
            />
          </div>
        </div>

        {/* Row 3: Required Services, Reference Link */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-700 block mb-1">Required Services</label>
            <div className="flex flex-wrap gap-4 border border-gray-200 p-2.5 rounded-lg bg-[#fafafa] h-10 items-center">
              <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.needCad}
                  onChange={(e) => setFormData({ ...formData, needCad: e.target.checked })}
                  className="rounded border-gray-300 text-[var(--orange-hover)] focus:ring-[var(--orange-primary)] w-3.5 h-3.5"
                />
                <span>Need CAD</span>
              </label>
              <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.needNda}
                  onChange={(e) => setFormData({ ...formData, needNda: e.target.checked })}
                  className="rounded border-gray-300 text-[var(--orange-hover)] focus:ring-[var(--orange-primary)] w-3.5 h-3.5"
                />
                <span>Need NDA Agreement?</span>
              </label>
              <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.brandLogo}
                  onChange={(e) => setFormData({ ...formData, brandLogo: e.target.checked })}
                  className="rounded border-gray-300 text-[var(--orange-hover)] focus:ring-[var(--orange-primary)] w-3.5 h-3.5"
                />
                <span>Brand Logo Engraving</span>
              </label>
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-700">Reference Link</label>
            <input
              type="text"
              placeholder="Google Drive / Dropbox / Website / Social Media link"
              value={formData.referenceLink}
              onChange={(e) => setFormData({ ...formData, referenceLink: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-[var(--orange-primary)] bg-[#fafafa]"
            />
          </div>
        </div>

        {/* Row 4: Custom Jewelry Notes */}
        <div className="space-y-1.5">
          <label className="text-xs font-bold text-gray-700">Custom Jewelry Notes</label>
          <textarea
            rows={5}
            placeholder="Describe your jewelry requirement, including estimated quantity, size, dimensions, gemstone details, finish, and any custom manufacturing notes"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            className="w-full px-4 py-3 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-[var(--orange-primary)] bg-[#fafafa] leading-relaxed resize-y"
          />
        </div>

        {/* Submit button */}
        <div className="pt-2">
          <button
            type="submit"
            disabled={loading}
            className="bg-black hover:bg-gray-900 text-white font-bold py-2.5 px-6 rounded-lg text-xs shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer uppercase tracking-wider"
          >
            {loading ? 'Sending Request...' : 'Send Custom Inquiry'}
          </button>
        </div>

      </form>
      )}
    </div>
  );
}
